import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';

const ContactPage = () => {
  useEffect(() => {
    document.title = 'Contact Bold Plans Advisory | Tax Services Jacksonville FL';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Contact Bold Plans Advisory in Jacksonville, Florida. Call (904) 331-5669 or email boldplansadvisory@gmail.com to schedule a free tax consultation.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`);

    const scriptId = 'schema-contact';
    document.getElementById(scriptId)?.remove();
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "name": "Contact Bold Plans Advisory",
      "description": "Contact Bold Plans Advisory in Jacksonville, Florida for expert tax preparation and financial advisory services.",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Bold Plans Advisory",
        "telephone": "+19043315669",
        "email": "boldplansadvisory@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Jacksonville",
          "addressRegion": "FL",
          "addressCountry": "US"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
            "opens": "09:00",
            "closes": "18:00"
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "10:00",
            "closes": "16:00"
          }
        ]
      }
    });
    document.head.appendChild(script);
    return () => { document.getElementById(scriptId)?.remove(); };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5goj99ia7n1eqcquh30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We will contact you within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitMessage('Submission failed. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        <section className="py-24 bg-gradient-to-br from-navy-800 to-navy-700 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="font-montserrat font-bold text-6xl mb-6">Get In Touch</h1>
            <p className="text-xl text-white/90 font-sans max-w-3xl mx-auto">
              Have questions about our services? Ready to schedule a consultation? We're here to help.
            </p>
          </div>
        </section>

        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="font-montserrat font-bold text-4xl text-gray-900 mb-6">
                  Let's Start a Conversation
                </h2>
                <p className="text-gray-600 font-sans text-lg mb-8 leading-relaxed">
                  Whether you need help with tax preparation, planning, or have questions about our services, our team of experts is ready to assist you. Fill out the form and we'll get back to you within 24 hours.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-500/10 rounded-full flex-shrink-0">
                      <i className="ri-phone-line text-orange-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-montserrat font-bold text-lg text-gray-900 mb-1">
                        Phone
                      </h3>
                      <p className="text-gray-600 font-sans">(904) 331-5669</p>
                      <p className="text-gray-500 font-sans text-sm">Mon-Fri 9am-6pm EST</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-500/10 rounded-full flex-shrink-0">
                      <i className="ri-mail-line text-orange-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-montserrat font-bold text-lg text-gray-900 mb-1">
                        Email
                      </h3>
                      <p className="text-gray-600 font-sans">boldplansadvisory@gmail.com</p>
                      <p className="text-gray-500 font-sans text-sm">We'll respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-500/10 rounded-full flex-shrink-0">
                      <i className="ri-map-pin-line text-orange-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-montserrat font-bold text-lg text-gray-900 mb-1">
                        Office
                      </h3>
                      <p className="text-gray-600 font-sans">Jacksonville, Florida</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-500/10 rounded-full flex-shrink-0">
                      <i className="ri-time-line text-orange-500 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-montserrat font-bold text-lg text-gray-900 mb-1">
                        Business Hours
                      </h3>
                      <p className="text-gray-600 font-sans">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-600 font-sans">Saturday: 10:00 AM - 4:00 PM</p>
                      <p className="text-gray-600 font-sans">Sunday: Closed</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="font-montserrat font-bold text-lg text-gray-900 mb-4">
                    Follow Us
                  </h3>
                  <div className="flex gap-3">
                    <a
                      href="https://linkedin.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer"
                      aria-label="LinkedIn"
                    >
                      <i className="ri-linkedin-fill text-xl"></i>
                    </a>
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer"
                      aria-label="Facebook"
                    >
                      <i className="ri-facebook-fill text-xl"></i>
                    </a>
                    <a
                      href="https://twitter.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer"
                      aria-label="Twitter"
                    >
                      <i className="ri-twitter-x-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-cream-50 rounded-3xl p-8 lg:p-12">
                <h3 className="font-montserrat font-bold text-3xl text-gray-900 mb-6">
                  Send Us a Message
                </h3>
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-6">
                  <div>
                    <label className="block text-gray-700 font-sans font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-sans text-sm bg-white"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-sans font-semibold mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-sans text-sm bg-white"
                        placeholder="john@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-sans font-semibold mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-sans text-sm bg-white"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-sans font-semibold mb-2">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-sans text-sm bg-white"
                    >
                      <option value="">Select a subject</option>
                      <option value="tax-preparation">Tax Preparation</option>
                      <option value="tax-planning">Tax Planning</option>
                      <option value="irs-representation">IRS Representation</option>
                      <option value="audit-support">Audit Support</option>
                      <option value="consultation">General Consultation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-sans font-semibold mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500 font-sans text-sm resize-none bg-white"
                      placeholder="Tell us about your tax needs..."
                    ></textarea>
                    <p className="text-gray-500 text-xs font-sans mt-1">
                      Maximum 500 characters
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-500 text-white py-4 rounded-full font-sans font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitMessage && (
                    <p className={`text-center font-sans ${submitMessage.includes('Thank') ? 'text-green-600' : 'text-red-600'}`}>
                      {submitMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3467.0234567890123!2d-81.65565!3d30.33218!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e5b716f1526b5f%3A0x7046a83f1b7b8c6e!2sJacksonville%2C%20FL!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              ></iframe>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;